import { apiClient, type StepUpRequestConfig } from "./client";

// Release 1.5.0 header (O-R52 search, O-R53 sync chip). Both answers are per person and sent no-store; they must never
// be persisted in the SPA's query cache either (the keys start with "header").

export interface GlobalSearchItem {
  id: string;
  title: string;
  subtitle: string | null;
  /** An SPA path the person's role may open (authorised per kind on the server). */
  to: string;
  badge: string | null;
  valueKind: string;
}

export interface GlobalSearchGroup {
  /** workOrders | parts | customers | workcenters | machines | purchaseOrders | knowledge | procedures */
  category: string;
  label: string;
  truncated: boolean;
  items: GlobalSearchItem[];
}

export interface GlobalSearchResponse {
  query: string;
  /** The exact key when the query was a work-order form with a suffix (600160-000 → 600160|000). */
  workOrderKey: string | null;
  groups: GlobalSearchGroup[];
  generatedAtUtc: string;
}

export type FreshnessState = "fresh" | "ageing" | "syncing" | "failed" | "waiting" | "practice";

export interface DataFreshnessSource {
  /** gss-full | gss-daily | gss-intraday | instruction-text | engineering */
  id: string;
  label: string;
  state: FreshnessState;
  /** Its age in words: "6 min ago", "Not yet", "Failed". */
  status: string;
  words: string;
  lastSuccessUtc: string | null;
  note: string | null;
}

/** The "GSS connection" box of the header sync menu (shared gateway and connection, D-O18). */
export interface DataFreshnessGss {
  mode: string;
  importRuns: boolean;
  gatewayName: string;
  connectionName: string;
  /** The governed refresh window in words. */
  refreshWindow: string;
  importStatus: string;
}

export interface DataFreshness {
  state: FreshnessState;
  label: string;
  words: string;
  ageMinutes: number | null;
  lastSuccessUtc: string | null;
  running: boolean;
  dataSource: string;
  sources: DataFreshnessSource[];
  /** Admin only: the header offers "Run sync now" (the existing signed Data sync request). */
  canRunSync: boolean;
  valueKind: string;
  generatedAtUtc: string;
  /** The last good read as plant-local "yyyy-MM-dd HH:mm". */
  dataAsOfPlant?: string | null;
  /** When the chip turns amber or red, in words. */
  ageRule?: string | null;
  gss?: DataFreshnessGss | null;
}

export const HEADER_QK = {
  all: ["header"] as const,
  search: (q: string) => ["header", "search", q] as const,
  freshness: ["header", "freshness"] as const,
  photo: ["header", "photo", "me"] as const,
  stepUp: ["header", "step-up"] as const
};

/** The current sign-in's authenticator step (the existing GET /session/mfa), for the account menu. */
export interface SigningSessionStatus {
  required: boolean;
  verified: boolean;
  bypassed: boolean;
  expiresAtUtc: string | null;
}

const noPrompt: StepUpRequestConfig = { skipStepUpPrompt: true };

export const headerApi = {
  search: async (q: string): Promise<GlobalSearchResponse> =>
    (await apiClient.get<GlobalSearchResponse>(`/search?q=${encodeURIComponent(q)}`)).data,
  freshness: async (): Promise<DataFreshness> => (await apiClient.get<DataFreshness>("/data/freshness")).data,
  /** The person's own photo (My profile), or null when there is none (204) or it cannot be read. */
  myPhoto: async (): Promise<Blob | null> => {
    try {
      const config: StepUpRequestConfig = { responseType: "blob", skipStepUpPrompt: true };
      const res = await apiClient.get<Blob>("/profile/me/photo", config);
      return res.status === 200 && res.data instanceof Blob && res.data.size > 0 ? res.data : null;
    } catch {
      return null;
    }
  },
  signingSession: async (): Promise<SigningSessionStatus> =>
    (await apiClient.get<SigningSessionStatus>("/session/mfa", noPrompt)).data,
  /** Ends THIS sign-in's authenticator proof on the server (release 1.5.0); the next privileged step asks for a code again. */
  endSigningSession: async (): Promise<SigningSessionStatus> =>
    (await apiClient.post<SigningSessionStatus>("/session/mfa/end", undefined, noPrompt)).data
};
